import { __assign, __read, __values } from "tslib";
import { eventToSentryRequest, getEnvelopeEndpointWithUrlEncodedAuth, getStoreEndpointWithUrlEncodedAuth, initAPIDetails, sessionToSentryRequest } from '@sentry/core';
import { dateTimestampInSeconds, dsnToString, eventStatusFromHttpCode, getGlobalObject, isDebugBuild, logger, makePromiseBuffer, parseRetryAfterHeader } from '@sentry/utils';
import { sendReport } from './utils';

function requestTypeToCategory(ty) {
  var tyStr = ty;
  return tyStr === 'event' ? 'error' : tyStr;
}

var global = getGlobalObject();
/** Base Transport class implementation */

var BaseTransport =
/** @class */
function () {
  function BaseTransport(options) {
    var _this = this;

    this.options = options;
    /** A simple buffer holding all requests. */

    this._buffer = makePromiseBuffer(30);
    /** Locks transport after receiving rate limits in a response */

    this._rateLimits = {};
    this._outcomes = {};
    this._api = initAPIDetails(options.dsn, options._metadata, options.tunnel); // eslint-disable-next-line deprecation/deprecation

    this.url = getStoreEndpointWithUrlEncodedAuth(this._api.dsn);

    if (this.options.sendClientReports && global.document) {
      global.document.addEventListener('visibilitychange', function () {
        if (global.document.visibilityState === 'hidden') {
          _this._flushOutcomes();
        }
      });
    }
  }
  /**
   * @inheritDoc
   */


  BaseTransport.prototype.sendEvent = function (event) {
    return this._sendRequest(eventToSentryRequest(event, this._api), event);
  };
  /**
   * @inheritDoc
   */


  BaseTransport.prototype.sendSession = function (session) {
    return this._sendRequest(sessionToSentryRequest(session, this._api), session);
  };
  /**
   * @inheritDoc
   */


  BaseTransport.prototype.close = function (timeout) {
    return this._buffer.drain(timeout);
  };
  /**
   * @inheritDoc
   */


  BaseTransport.prototype.recordLostEvent = function (reason, category) {
    var _a;

    if (!this.options.sendClientReports) {
      return;
    } // We want to track each category (event, transaction, session) separately
    // but still keep the distinction between different type of outcomes.
    // We could use nested maps, but it's much easier to read and type this way.
    // A correct type for map-based implementation if we want to go that route
    // would be `Partial<Record<SentryRequestType, Partial<Record<Outcome, number>>>>`


    var key = requestTypeToCategory(category) + ":" + reason;
    logger.log("Adding outcome: " + key);
    this._outcomes[key] = (_a = this._outcomes[key], _a !== null && _a !== void 0 ? _a : 0) + 1;
  };
  /**
   * Send outcomes as an envelope
   */


  BaseTransport.prototype._flushOutcomes = function () {
    if (!this.options.sendClientReports) {
      return;
    }

    var outcomes = this._outcomes;
    this._outcomes = {}; // Nothing to send

    if (!Object.keys(outcomes).length) {
      logger.log('No outcomes to flush');
      return;
    }

    logger.log("Flushing outcomes:\n" + JSON.stringify(outcomes, null, 2));
    var url = getEnvelopeEndpointWithUrlEncodedAuth(this._api.dsn, this._api.tunnel); // Envelope header is required to be at least an empty object

    var envelopeHeader = JSON.stringify(__assign({}, this._api.tunnel && {
      dsn: dsnToString(this._api.dsn)
    }));
    var itemHeaders = JSON.stringify({
      type: 'client_report'
    });
    var item = JSON.stringify({
      timestamp: dateTimestampInSeconds(),
      discarded_events: Object.keys(outcomes).map(function (key) {
        var _a = __read(key.split(':'), 2),
            category = _a[0],
            reason = _a[1];

        return {
          reason: reason,
          category: category,
          quantity: outcomes[key]
        };
      })
    });
    var envelope = envelopeHeader + "\n" + itemHeaders + "\n" + item;

    try {
      sendReport(url, envelope);
    } catch (e) {
      logger.error(e);
    }
  };
  /**
   * Handle Sentry repsonse for promise-based transports.
   */


  BaseTransport.prototype._handleResponse = function (_a) {
    var requestType = _a.requestType,
        response = _a.response,
        headers = _a.headers,
        resolve = _a.resolve,
        reject = _a.reject;
    var status = eventStatusFromHttpCode(response.status);
    /**
     * "The name is case-insensitive."
     * https://developer.mozilla.org/en-US/docs/Web/API/Headers/get
     */

    var limited = this._handleRateLimit(headers);

    if (limited && isDebugBuild()) {
      logger.warn("Too many " + requestType + " requests, backing off until: " + this._disabledUntil(requestType));
    }

    if (status === 'success') {
      resolve({
        status: status
      });
      return;
    }

    reject(response);
  };
  /**
   * Gets the time that given category is disabled until for rate limiting
   */


  BaseTransport.prototype._disabledUntil = function (requestType) {
    var category = requestTypeToCategory(requestType);
    return this._rateLimits[category] || this._rateLimits.all;
  };
  /**
   * Checks if a category is rate limited
   */


  BaseTransport.prototype._isRateLimited = function (requestType) {
    return this._disabledUntil(requestType) > new Date(Date.now());
  };
  /**
   * Sets internal _rateLimits from incoming headers. Returns true if headers contains a non-empty rate limiting header.
   */


  BaseTransport.prototype._handleRateLimit = function (headers) {
    var e_1, _a, e_2, _b;

    var now = Date.now();
    var rlHeader = headers['x-sentry-rate-limits'];
    var raHeader = headers['retry-after'];

    if (rlHeader) {
      try {
        // rate limit headers are of the form
        //     <header>,<header>,..
        // where each <header> is of the form
        //     <retry_after>: <categories>: <scope>: <reason_code>
        // where
        //     <retry_after> is a delay in ms
        //     <categories> is the event type(s) (error, transaction, etc) being rate limited and is of the form
        //         <category>;<category>;...
        //     <scope> is what's being limited (org, project, or key) - ignored by SDK
        //     <reason_code> is an arbitrary string like "org_quota" - ignored by SDK
        for (var _c = __values(rlHeader.trim().split(',')), _d = _c.next(); !_d.done; _d = _c.next()) {
          var limit = _d.value;
          var parameters = limit.split(':', 2);
          var headerDelay = parseInt(parameters[0], 10);
          var delay = (!isNaN(headerDelay) ? headerDelay : 60) * 1000; // 60sec default

          try {
            for (var _e = (e_2 = void 0, __values(parameters[1].split(';'))), _f = _e.next(); !_f.done; _f = _e.next()) {
              var category = _f.value;
              this._rateLimits[category || 'all'] = new Date(now + delay);
            }
          } catch (e_2_1) {
            e_2 = {
              error: e_2_1
            };
          } finally {
            try {
              if (_f && !_f.done && (_b = _e.return)) _b.call(_e);
            } finally {
              if (e_2) throw e_2.error;
            }
          }
        }
      } catch (e_1_1) {
        e_1 = {
          error: e_1_1
        };
      } finally {
        try {
          if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
        } finally {
          if (e_1) throw e_1.error;
        }
      }

      return true;
    } else if (raHeader) {
      this._rateLimits.all = new Date(now + parseRetryAfterHeader(now, raHeader));
      return true;
    }

    return false;
  };

  return BaseTransport;
}();

export { BaseTransport };